import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import {
  CurrencyCode,
  enumCurrencyCode,
  enumInvoiceEntityReferenceType,
  enumInvoiceStatus,
  enumPaymentTerm,
  enumSalesLoadLineItemPhase,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { invoiceCreateFromReconciliationAtom } from '../_graphql/mutationInvoiceCreateFromReconciliation';
import {
  bookingLoadsForInvoiceQueryAtom,
  BookingLoadForInvoice,
} from '../_graphql/queryBookingLoadsForInvoice';
import {
  transformLoadLineItemsToInvoiceLineItems,
  calculateInvoiceTotal,
  getInvoiceCurrencyCode,
  InvoiceLineItemInput,
  LoadLineItemForInvoice,
} from '../utils/invoiceLineItems';

// Plain atoms (Form State)
export const $bookingInvoiceBookingId = atom<string | null>(null);

export const $bookingInvoiceForm = atom<{
  documentDate: CalendarDate | null;
}>({
  documentDate: today(getLocalTimeZone()),
});

export const $isBookingInvoiceDialogOpen = atom<boolean>(false);
export const $isCreatingInvoices = atom<boolean>(false);

// Action atoms (Form Updates)
export const $setBookingInvoiceBookingId = atom(
  null,
  (get, set, bookingId: string | null) => {
    set($bookingInvoiceBookingId, bookingId);
  },
);

export const $updateBookingInvoiceForm = atom(
  null,
  (
    get,
    set,
    updates: Partial<{
      documentDate: CalendarDate | null;
    }>,
  ) => {
    const currentForm = get($bookingInvoiceForm);
    set($bookingInvoiceForm, { ...currentForm, ...updates });
  },
);

export const $resetBookingInvoiceForm = atom(null, (get, set) => {
  set($bookingInvoiceForm, {
    documentDate: today(getLocalTimeZone()),
  });
  set($bookingInvoiceBookingId, null);
  set($isBookingInvoiceDialogOpen, false);
  set($isCreatingInvoices, false);
});

export const $openBookingInvoiceDialog = atom(
  null,
  (get, set, bookingId: string) => {
    set($bookingInvoiceBookingId, bookingId);
    set($bookingInvoiceForm, {
      documentDate: today(getLocalTimeZone()),
    });
    set($isBookingInvoiceDialogOpen, true);
  },
);

export const $closeBookingInvoiceDialog = atom(null, (get, set) => {
  set($isBookingInvoiceDialogOpen, false);
  set($bookingInvoiceBookingId, null);
});

// Query atoms (Booking Loads for Invoice)
export const $bookingLoadsForInvoice = bookingLoadsForInvoiceQueryAtom(
  (get) => ({
    variables: { bookingId: get($bookingInvoiceBookingId) ?? '' },
    skip: !get($bookingInvoiceBookingId),
  }),
);

// Computed atoms (Booking Info)
export const $bookingInvoiceData = atom((get) => {
  const data = get($bookingLoadsForInvoice);
  return data?.data ?? null;
});

export const $bookingInvoiceLoading = atom((get) => {
  const data = get($bookingLoadsForInvoice);
  return data?.isLoading ?? false;
});

// Computed atoms (Group Loads by Customer)
export type CustomerInvoiceGroup = {
  accountId: string;
  accountName: string;
  facilityId: string;
  paymentTerm: PaymentTerm;
  loads: BookingLoadForInvoice[];
};

export const $loadsByCustomer = atom((get) => {
  const data = get($bookingInvoiceData);
  if (!data) return new Map<string, CustomerInvoiceGroup>();

  const loads = data.loads;
  const grouped = new Map<string, CustomerInvoiceGroup>();

  loads.forEach((load) => {
    const accountId = load.accountId;
    if (!accountId) return;

    const existing = grouped.get(accountId);
    if (existing) {
      existing.loads.push(load);
    } else {
      grouped.set(accountId, {
        accountId,
        accountName: load.accountName ?? 'Unknown Customer',
        facilityId: load.facilityId,
        paymentTerm: (load.paymentTerm as PaymentTerm) ?? enumPaymentTerm.Net30,
        loads: [load],
      });
    }
  });

  return grouped;
});

// Computed atoms (Invoice Previews per Customer)
export type CustomerInvoicePreview = {
  accountId: string;
  accountName: string;
  facilityId: string;
  paymentTerm: PaymentTerm;
  lineItems: InvoiceLineItemInput[];
  totalAmount: number;
  currencyCode: CurrencyCode;
  loadPreviews: Array<{
    documentNumber: string;
    lineItems: InvoiceLineItemInput[];
    subtotal: number;
  }>;
};

type PreviewGenerationResult = {
  previews: Map<string, CustomerInvoicePreview>;
  error: string | null;
};

function generatePreviewsForCustomer(
  accountId: string,
  accountName: string,
  facilityId: string,
  paymentTerm: PaymentTerm,
  loads: BookingLoadForInvoice[],
): CustomerInvoicePreview {
  const loadPreviews: CustomerInvoicePreview['loadPreviews'] = [];
  const allLineItems: InvoiceLineItemInput[] = [];
  let currentSortOrder = 0;

  for (const load of loads) {
    const shippedLineItems = load.lineItems.filter(
      (li) => li.phase === enumSalesLoadLineItemPhase.Shipped,
    );

    const lineItemsForInvoice: LoadLineItemForInvoice[] = shippedLineItems.map(
      (li) => ({
        id: li.id,
        itemId: li.itemId,
        itemName: li.item?.name ?? 'Unknown Item',
        weights: li.weights,
        unitPrice: li.unitPrice,
        pricingUnit: li.pricingUnit,
      }),
    );

    const groupName = `Load #${load.documentNumber}`;
    const transformedLineItems = transformLoadLineItemsToInvoiceLineItems(
      lineItemsForInvoice,
      groupName,
      currentSortOrder,
    );

    const loadSubtotal = calculateInvoiceTotal(transformedLineItems);

    if (loadSubtotal < 0) {
      throw new Error(
        `Invalid negative subtotal for Load #${load.documentNumber}`,
      );
    }

    loadPreviews.push({
      documentNumber: load.documentNumber,
      lineItems: transformedLineItems,
      subtotal: loadSubtotal,
    });

    allLineItems.push(...transformedLineItems);
    currentSortOrder += transformedLineItems.length;
  }

  const totalAmount = calculateInvoiceTotal(allLineItems);
  const currencyCode =
    allLineItems.length > 0
      ? getInvoiceCurrencyCode(allLineItems)
      : enumCurrencyCode.USD;

  return {
    accountId,
    accountName,
    facilityId,
    paymentTerm,
    lineItems: allLineItems,
    totalAmount,
    currencyCode,
    loadPreviews,
  };
}

export const $invoicePreviewsResult = atom((get): PreviewGenerationResult => {
  const loadsByCustomer = get($loadsByCustomer);
  const previews = new Map<string, CustomerInvoicePreview>();

  try {
    for (const [
      accountId,
      { accountName, facilityId, paymentTerm, loads },
    ] of loadsByCustomer) {
      const preview = generatePreviewsForCustomer(
        accountId,
        accountName,
        facilityId,
        paymentTerm,
        loads,
      );
      previews.set(accountId, preview);
    }

    return { previews, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to generate invoice preview';
    return { previews: new Map(), error: errorMessage };
  }
});

export const $invoicePreviewsByCustomer = atom(
  (get) => get($invoicePreviewsResult).previews,
);

export const $invoicePreviewError = atom(
  (get) => get($invoicePreviewsResult).error,
);

// Computed atoms (Validation per Customer)
export type BookingInvoiceValidation = {
  isValid: boolean;
  errors: string[];
};

export const $bookingInvoiceValidation = atom(
  (get): BookingInvoiceValidation => {
    const loadsByCustomer = get($loadsByCustomer);
    const form = get($bookingInvoiceForm);
    const previewResult = get($invoicePreviewsResult);
    const errors: string[] = [];

    if (previewResult.error) {
      errors.push(previewResult.error);
    }

    if (!form.documentDate) {
      errors.push('Invoice date is required');
    }

    if (loadsByCustomer.size === 0) {
      errors.push('No loads available for invoicing');
      return { isValid: false, errors };
    }

    loadsByCustomer.forEach(({ accountName, loads }) => {
      loads.forEach((load) => {
        if (load.reconciliation?.invoiceId) {
          errors.push(
            `Load #${load.documentNumber} (${accountName}) already has an invoice`,
          );
        }
      });

      const hasLineItemsWithPricing = loads.some((l) =>
        l.lineItems.some(
          (li) =>
            li.phase === enumSalesLoadLineItemPhase.Shipped &&
            li.unitPrice != null,
        ),
      );
      if (!hasLineItemsWithPricing) {
        errors.push(`No priced shipped line items for ${accountName}`);
      }
    });

    return { isValid: errors.length === 0, errors };
  },
);

// Computed atoms (Summary)
export const $bookingInvoiceSummary = atom((get) => {
  const previews = get($invoicePreviewsByCustomer);
  const customerCount = previews.size;
  const totalsByCurrency = new Map<CurrencyCode, number>();
  let totalLineItems = 0;

  previews.forEach((preview) => {
    const currentTotal = totalsByCurrency.get(preview.currencyCode) ?? 0;
    totalsByCurrency.set(
      preview.currencyCode,
      currentTotal + preview.totalAmount,
    );
    totalLineItems += preview.lineItems.length;
  });

  return {
    customerCount,
    totalsByCurrency,
    totalLineItems,
  };
});

// Action atoms (Create Invoices)
export const $createInvoice = invoiceCreateFromReconciliationAtom();

export type CreateBookingInvoicesResult = Array<{
  accountId: string;
  accountName: string;
  invoiceId: string;
  documentNumber: string;
  amount: number;
  currencyCode: CurrencyCode;
}>;

export type CreateBookingInvoicesError = {
  successfulInvoices: CreateBookingInvoicesResult;
  failedCustomers: Array<{
    accountId: string;
    accountName: string;
    error: string;
  }>;
};

export const $createBookingInvoices = atom(
  null,
  async (get, set): Promise<CreateBookingInvoicesResult> => {
    const isAlreadyCreating = get($isCreatingInvoices);
    if (isAlreadyCreating) {
      throw new Error('Invoice creation already in progress');
    }

    const loadsByCustomer = get($loadsByCustomer);
    const form = get($bookingInvoiceForm);
    const invoicePreviews = get($invoicePreviewsByCustomer);
    const data = get($bookingInvoiceData);

    const documentDate = form.documentDate;
    if (!documentDate) {
      throw new Error('Invoice date is required');
    }

    set($isCreatingInvoices, true);

    const results: CreateBookingInvoicesResult = [];
    const failures: CreateBookingInvoicesError['failedCustomers'] = [];

    try {
      const invoicePromises = Array.from(loadsByCustomer.entries()).map(
        async ([accountId, group]) => {
          const preview = invoicePreviews.get(accountId);
          if (!preview || preview.lineItems.length === 0) {
            return null;
          }

          const firstLoadId = group.loads[0]?.id;
          if (!firstLoadId) {
            return null;
          }

          try {
            const sourceReferences = group.loads.map((load) => ({
              entityId: load.id,
              entityType: enumInvoiceEntityReferenceType.OutboundLoad,
            }));

            const result = await set($createInvoice, {
              loadId: firstLoadId,
              accountId,
              facilityId: group.facilityId,
              documentDate: documentDate.toString(),
              paymentTerm: group.paymentTerm,
              amount: preview.totalAmount,
              currencyCode: preview.currencyCode,
              notes: `Invoice for booking #${data?.bookingNumber ?? ''} - ${group.accountName}`,
              lineItems: preview.lineItems,
              status: enumInvoiceStatus.ProForma,
              sourceReferences,
            });

            return {
              success: true as const,
              accountId,
              accountName: group.accountName,
              invoiceId: result.financeInvoiceCreate.id,
              documentNumber: result.financeInvoiceCreate.documentNumber,
              amount: preview.totalAmount,
              currencyCode: preview.currencyCode,
            };
          } catch (error) {
            return {
              success: false as const,
              accountId,
              accountName: group.accountName,
              error:
                error instanceof Error
                  ? error.message
                  : 'Unknown error creating invoice',
            };
          }
        },
      );

      const invoiceResults = await Promise.all(invoicePromises);

      for (const result of invoiceResults) {
        if (result === null) continue;

        if (result.success) {
          results.push({
            accountId: result.accountId,
            accountName: result.accountName,
            invoiceId: result.invoiceId,
            documentNumber: result.documentNumber,
            amount: result.amount,
            currencyCode: result.currencyCode,
          });
        } else {
          failures.push({
            accountId: result.accountId,
            accountName: result.accountName,
            error: result.error,
          });
        }
      }

      if (failures.length > 0 && results.length === 0) {
        throw new Error(
          `Failed to create invoices: ${failures.map((f) => `${f.accountName}: ${f.error}`).join('; ')}`,
        );
      }

      if (failures.length > 0) {
        console.error('Some invoices failed to create:', failures);
      }

      return results;
    } finally {
      set($isCreatingInvoices, false);
    }
  },
);
